import { Dispatch } from 'redux'

import {
  DealerC,
  IUniToast_Alert,
  IMultiInputUpdate
} from '@unikey/unikey-commons/release/comm'

import {
  mockAuth,
  api,
  attemptRetrieveDealerDetails,
  ENavPages,
  navConfig,
  isLocationRootUrl,
  attemptRetrieveAvailableSubscriptionModels,
  clearAllAuthValues,
} from '../../internal'
import { IPendingRedirectState } from '../reducers/portalReducers'

export enum portalActions {

  ADD_ALERT = 'CREATE_ALERT',
  DISMISS_ALERT = 'DISMISS_ALERT',
  DISMISS_ALL_ALERTS = 'DISMISS_ALL_ALERT',
  SHOW_ALL_ALERTS = 'SHOW_ALL_ALERTS',
  HIDE_ALL_ALERTS = 'HIDE_ALL_ALERTS',
  WIPE_ALL_ALERTS = 'WIPE_ALL_ALERTS',
  CAPTCHA_CHANGE = 'CAPTCHA_CHANGE',
  TOGGLE_AVATAR_MENU = 'TOGGLE_AVATAR_MENU',
  TOGGLE_DEALER_SWITCHER = 'TOGGLE_DEALER_SWITCHER',
  UPDATE_LOCALE = 'UPDATE_LOCALE',
  UPDATE_LOCALE_INPUT = 'UPDATE_LOCALE_INPUT',
  NAVIGATION_CHANGE = 'NAVIGATION_CHANGE',
  REDIRECT_TO_LOGIN = 'REDIRECT_TO_LOGIN',
  REDIRECT_TO_LOGOUT = 'REDIRECT_TO_LOGOUT',
  REDIRECT_TO_VERIFY = 'REDIRECT_TO_VERIFY',
  REDIRECT_TO_PORTAL = 'REDIRECT_TO_PORTAL',
  REDIRECT_NO_REDUCER = 'REDIRECT_NO_REDUCER',
  CLEAR_APPLICATION_STATE = 'CLEAR_APPLICATION_STATE',
  SET_DARK_MODE_THEME = 'SET_DARK_MODE_THEME',
  TOGGLE_FEEDBACK_MODAL = 'TOGGLE_FEEDBACK_MODAL',
  UPDATE_FEEDBACK = 'UPDATE_FEEDBACK',
  RESET_FEEDBACK = 'RESET_FEEDBACK',
  DEALER_CONTEXT_SET = 'DEALER_CONTEXT_SET',
  DEALER_TRANSITION_COMPLETE = 'DEALER_TRANSITION_COMPLETE',

  ADD_PENDING_REDIRECT = 'ADD_PENDING_REDIRECT',
  CLEAR_PENDING_REDIRECT = 'CLEAR_PENDING_REDIRECT'
  
}

export function setDarkModeTheme(theme: string) {
  return {
    type: portalActions.SET_DARK_MODE_THEME,
    theme
  }
}

export function addAlert(alertInfo: IUniToast_Alert) {
  return {
    type: portalActions.ADD_ALERT,
    alert: alertInfo,
  }
}

export function showAllAlerts() {
  return {
    type: portalActions.SHOW_ALL_ALERTS
  }
}

export function hideAllAlerts() {
  return {
    type: portalActions.HIDE_ALL_ALERTS
  }
}

export function dismissAllAlerts() {
  return {
    type: portalActions.DISMISS_ALL_ALERTS,
  }
}

export function dismissAlert(clearFromCache: boolean, alertId: number) {
  return {
    type: portalActions.DISMISS_ALERT,
    alertId,
    clearFromCache
  }
}

export function wipeAllAlerts() {
  return {
    type: portalActions.WIPE_ALL_ALERTS
  }
}

export function toggleFeedbackModal() {
  return {
    type: portalActions.TOGGLE_FEEDBACK_MODAL
  }
}

export function updateFeedback(feedback: IMultiInputUpdate) {
  return {
    type: portalActions.UPDATE_FEEDBACK,
    feedback
  }
}

export function resetFeedbackForm() {
  return {
    type: portalActions.RESET_FEEDBACK,
  }
}


export function updateCaptcha(captchaValue: string | null) {
  return {
    type: portalActions.CAPTCHA_CHANGE,
    value: captchaValue
  }
}

export function languageChange(localeCode: string) {
  return {
    type: portalActions.UPDATE_LOCALE,
    localeCode
  }
}

export function localeInputChange(localeInput: string) {
  return {
    type: portalActions.UPDATE_LOCALE_INPUT,
    localeInput
  }
}

export function navChange(newPath: string) {
  return {
    type: portalActions.NAVIGATION_CHANGE,
    newPath
  }
}

export function toggleAvatarMenu(open?: boolean) {
  return {
    type: portalActions.TOGGLE_AVATAR_MENU,
    open
  }
}

export function toggleDealerSwitcher(open?: boolean) {
  return {
    type: portalActions.TOGGLE_DEALER_SWITCHER,
    open
  }
}

export function setActivePortalDealer(dealer: DealerC, changingDealers: boolean = false) {
  return async (dispatch: Dispatch<any>, getState: any): Promise<any> => {
    const currentDealerId = getState()?.portal?.activeDealer?.id;
    // reset dealer-scoped state when the caller signals an explicit switch OR the
    // dealer id actually changed. the id check guards against a redundant
    // same-dealer call (e.g. the second token refresh right after login) that
    // would otherwise re-dispatch DEALER_CONTEXT_SET and wipe the loaded org list.
    const shouldSetDealerContext = changingDealers || currentDealerId !== dealer?.id;

    api.setDealer(dealer);

    if (shouldSetDealerContext) {
      // changingDealers still drives the transitioning flag (via the reducer) and
      // the landing redirect below — i.e. it means "the user switched dealers",
      // distinct from the initial/passive set on login.
      dispatch({
        type: portalActions.DEALER_CONTEXT_SET,
        dealer,
        changingDealers
      })

      if (changingDealers) {
        dispatch(addPendingRedirect({
          pathname: navConfig.get(ENavPages.portalLanding)!.linkTo([])
        }));
      }
    }
  };
}


export function finishTransitioningDealer(dealerId: string) {
  return async (dispatch: Dispatch<any>, getState: any): Promise<any> => {
    dispatch({
      type: portalActions.DEALER_TRANSITION_COMPLETE,
      dealerId,
    });
  };
}

export function resetApplicationStateWithDarkModeTheme() {
  return (dispatch: Dispatch<any>, getState: any): any => {
    const currentDarkModeTheme = getState()?.portal?.darkModeTheme;
    dispatch(clearApplicationState())
    dispatch(setDarkModeTheme(currentDarkModeTheme));
  }  
}

export function clearApplicationState() {
  clearAllAuthValues();
  return {
    type: portalActions.CLEAR_APPLICATION_STATE
  }
}

export interface IRedirectOptions {
  preventContinue: boolean,
  continuePath?: string
}

export function redirectToLogin(options?: IRedirectOptions) {
  return (dispatch: Dispatch<any>, getState: any): any => {
    if (!mockAuth) {
      // encode the desired path (not the leading #)
      var continuePath = location.hash.slice(1);
      if (options?.continuePath) {
        continuePath = options?.continuePath;
      }

      // The default landing page is where the app forwards a fresh, hash-less
      // return from OIDC before auth state is hydrated. Treat it (and an empty
      // path) as "no meaningful destination" so a race back into login doesn't
      // synthesize a continue that clobbers a real cached continueTo value.
      const defaultLandingPath = navConfig.get(ENavPages.portalLanding)!.linkTo();
      const isDefaultOrEmpty = !continuePath || continuePath === defaultLandingPath;

      // encode the desired continue path for url safe query parameters
      continuePath = encodeURIComponent(continuePath);

      if (!continuePath.includes('login') && !continuePath.includes('logout')) {
        dispatch(resetApplicationStateWithDarkModeTheme());
        // prevent in instance where we dont want to use, ex: redirect to login after verify email
        if (options?.preventContinue || isDefaultOrEmpty) {
          dispatch(addPendingRedirect({
            pathname: navConfig.get(ENavPages.login)!.linkTo([])
          }));
        } else {
          dispatch(addPendingRedirect({
            pathname: navConfig.get(ENavPages.login)!.linkTo([]),
            search: `?continue=${continuePath}`
          }));
        }
      } else {
        dispatch(addPendingRedirect({
          pathname: navConfig.get(ENavPages.login)!.linkTo([]),
          search: ''
        }));
      }
    }

    return {
      type: portalActions.REDIRECT_TO_LOGIN
    }
  }
}

export function redirectToLogout(preventContinue: boolean = false) {
  return (dispatch: Dispatch<any>, getState: any): any => {
    // encode the desired path (not the leading #)
    const continuePath = encodeURIComponent(location.hash.slice(1));
    if (!continuePath.includes('login') && !continuePath.includes('logout')) {
      dispatch(resetApplicationStateWithDarkModeTheme());
      dispatch(addPendingRedirect({
        pathname: navConfig.get(ENavPages.logout)?.linkTo([]),
        search: preventContinue ? '' : `?continue=${continuePath}`
      }));
    }
    return {
      type: portalActions.REDIRECT_TO_LOGOUT
    }
  }
}

export function redirectToPortalLandingPage() {
  return (dispatch: Dispatch<any>, getState: any): any => {

    if (!mockAuth) {
      dispatch(addPendingRedirect({ pathname: navConfig.get(ENavPages.portalLanding)!.linkTo([]) }));
    }
    return {
      type: portalActions.REDIRECT_TO_PORTAL
    }
  }
}

export function checkValidAuthentication(): any {
  return (dispatch: Dispatch<any>, getState: any): boolean => {
    const foundToken = api.getAccessToken();

    if (!foundToken) {
      return false;
    }
    return true;
  }
}
export interface IPendingRedirectActionParams extends IPendingRedirectState {}

export function addPendingRedirect(params: IPendingRedirectActionParams) {
  return {
    type: portalActions.ADD_PENDING_REDIRECT,
    ...params
  }
}

export function clearPendingRedirect() {
  return {
    type: portalActions.CLEAR_PENDING_REDIRECT,
  }
}